import { create } from 'zustand';
import RAGApiService from '../services/ragApiService';
import { getTaskPollingService, TaskPollingOptions } from '../services/taskPollingService';
import { getValidatedApiConfig } from '../utils/apiConfig';
import { 
  RAGContext, 
  QueryRequest, 
  QueryResponse, 
  ChunkTool,
  Language,
  FileUpload,
  ProtectedKeywordsData,
  CustomJsonData,
  TaskListResponse,
  ChunkResponse
} from '../types/rag';

// Global ref to store the upload success callback
let uploadSuccessCallback: (() => void) | null = null;

// Global function to set the upload success callback
export const setUploadSuccessCallback = (callback: (() => void) | null) => {
  uploadSuccessCallback = callback;
};

export const AVAILABLE_CHUNK_TOOLS: ChunkTool[] = [
  { name: 'classic', displayName: 'Classic(Text Only)' },
  { name: 'deepdoc', displayName: 'DeepDoc(OCR)' },
  { name: 'llm', displayName: 'LLM(Semantic, experimental)' }
];
export const AVAILABLE_LANGUAGES: Language[] = ['ko', 'ja', 'zh', 'en'];

interface RAGState {
  // State
  isLoading: boolean;
  errorMessage: string | null;
  contexts: RAGContext[];
  currentContext: RAGContext | null;
  lastUserQuery: string;
  lastResponse: QueryResponse | null;
  selectedChunkTool: ChunkTool;
  selectedLanguage: Language;
  baseUrl: string;
  fileUploads: FileUpload[];
  
  // Global operation state
  isOperationInProgress: boolean;
  currentOperation: string | null;

  queueStatus: any | null;

  // Retrieval parameters — query-time knobs shared across the Query/Ask tabs and the
  // Retrieval Settings card. Defaults track the backend upgrade (reranker ON, cosine
  // floor 0.60). alpha 0.5 here; when a caller omits alpha the backend applies its
  // Korean-aware default (0.7 for ko). See eval/IR_BENCHMARK.md.
  retrievalParams: {
    topK: number;
    alpha: number;
    scoreThreshold: number;
    fileRetrievalK: number;
    fileSimilarityThreshold: number;
    contextWindow: number;
    useReranker: boolean;
  };

  // Actions
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setContexts: (contexts: RAGContext[]) => void;
  setCurrentContext: (context: RAGContext | null) => void;
  setLastUserQuery: (query: string) => void;
  setLastResponse: (response: QueryResponse | null) => void;
  setSelectedChunkTool: (tool: ChunkTool) => void;
  setSelectedLanguage: (language: Language) => void;
  setBaseUrl: (url: string) => void;
  addFileUpload: (upload: FileUpload) => void;
  removeFileUpload: (id: string) => void;
  updateFileUpload: (id: string, updates: Partial<FileUpload>) => void;
  
  setRetrievalParams: (partial: Partial<{
    topK: number;
    alpha: number;
    scoreThreshold: number;
    fileRetrievalK: number;
    fileSimilarityThreshold: number;
    contextWindow: number;
    useReranker: boolean;
  }>) => void;

  // Global operation state actions
  setOperationInProgress: (inProgress: boolean, operation?: string) => void;
  
  // API Actions
  loadContexts: () => Promise<void>;
  refreshCurrentContext: () => Promise<void>;
  setContextualRetrieval: (enabled: boolean, reembed: boolean, onProgress?: (done: number, total: number) => void) => Promise<void>;
  createContext: (name: string) => Promise<void>;
  deleteContext: (contextId: string) => Promise<void>;
  uploadFile: (file: File) => Promise<void>;
  uploadCustomJson: (jsonData: CustomJsonData) => Promise<void>;
  getCustomJson: () => Promise<CustomJsonData | null>;
  uploadProtectedKeywords: (keywords: ProtectedKeywordsData) => Promise<void>;
  uploadKeywordMappings: (mappings: { mappings: Record<string, { patterns: string[]; replacement: string[] }> }) => Promise<void>;
  downloadKeywordMappings: () => Promise<void>;
  getChunks: (itemId: string) => Promise<ChunkResponse|any>;
  uploadChunks: (itemId: string, editChunk?: any) => Promise<void>;
  buildChunks: (itemId: string) => Promise<any>;
  buildEmbeddings: (itemId: string, editChunk?: any) => Promise<void>;
  getQueueStatus: () => Promise<void>;
  getAllTasks: (limit?: number) => Promise<any>;
  deleteItem: (itemId: string) => Promise<void>;
  query: (request: QueryRequest) => Promise<void>;
  
  
  // Utility Actions
  clearCurrentContext: () => void;
}

// Get API base URL with validation
const apiConfig = getValidatedApiConfig();
const RAG_API_BASE: string = apiConfig.baseUrl;

export const useRAGStore = create<RAGState>((set, get) => ({
  // Initial state
  isLoading: false,
  errorMessage: null,
  contexts: [], // Ensure this is always an array
  currentContext: null,
  lastUserQuery: '',
  lastResponse: null,
  selectedChunkTool: { name: 'classic', displayName: 'Classic' },
  selectedLanguage: 'ko',
  baseUrl: RAG_API_BASE,
  fileUploads: [],

  queueStatus: null,

  retrievalParams: {
    topK: 5,
    alpha: 0.7,            // Korean-calibrated fusion weight (dense-heavy); en can lower toward 0.5
    scoreThreshold: 0.6,   // Korean-calibrated cosine floor (see IR_BENCHMARK.md; en prefers 0.55)
    fileRetrievalK: 0,     // stage 1: 0 = no file-count cap (a fixed 5 silently dropped files on multi-file contexts)
    fileSimilarityThreshold: 0.2,  // stage 1: cliff sits between 0.25 and 0.20 on the 23-file novasemi set
    contextWindow: 0,
    useReranker: true,     // reranker default ON (large, robust gain)
  },

  // Global operation state
  isOperationInProgress: false,
  currentOperation: null,

  // State setters
  setLoading: (loading) => set({ isLoading: loading }),
  setError: (error) => set({ errorMessage: error }),
  setRetrievalParams: (partial) => set((state) => ({ retrievalParams: { ...state.retrievalParams, ...partial } })),
  setContexts: (contexts) => {
    const safeContexts = contexts || [];
    set({ contexts: safeContexts });
  },
  setCurrentContext: (context) => set({ currentContext: context }),
  setLastUserQuery: (query) => set({ lastUserQuery: query }),
  setLastResponse: (response) => set({ lastResponse: response }),
  setSelectedChunkTool: (tool) => set({ selectedChunkTool: tool }),
  setSelectedLanguage: (language) => set({ selectedLanguage: language }),
  setBaseUrl: (url) => set({ baseUrl: url }),
  addFileUpload: (upload) => set((state) => ({ 
    fileUploads: [...state.fileUploads, upload] 
  })),
  removeFileUpload: (id) => set((state) => ({ 
    fileUploads: state.fileUploads.filter(u => u.id !== id) 
  })),
  updateFileUpload: (id, updates) => set((state) => ({
    fileUploads: state.fileUploads.map(u => 
      u.id === id ? { ...u, ...updates } : u
    )
  })),
  
  // Global operation state setters
  setOperationInProgress: (inProgress, operation) => set({ 
    isOperationInProgress: inProgress, 
    currentOperation: inProgress ? operation || null : null 
  }),
  
  // API Actions
  loadContexts: async () => {
    const { setLoading, setError, setContexts, baseUrl } = get();
    setLoading(true);
    setError(null);
    
    try {
      const apiService = new RAGApiService(baseUrl);
      const contextIds = await apiService.listContexts();
      
      const contexts: RAGContext[] = [];
      
      for (const contextId of contextIds) {
        try {
          const context = await apiService.getContextDetails(contextId);
          contexts.push(context);
        } catch (error) {
          console.warn(`Failed to load context ${contextId}:`, error);
        }
      }
      
       setContexts(contexts);
       
       // Don't automatically select the first context - user must choose manually
    } catch (error) {
      console.error('Error loading contexts:', error);
      setError(error instanceof Error ? error.message : 'Failed to load contexts');
      // Don't reset contexts on error to prevent state loss
      // setContexts([]);
    } finally {
      setLoading(false);
    }
  },

  // Refresh only the current context (faster than reloading all contexts)
  refreshCurrentContext: async () => {
    const { setError, baseUrl, currentContext, setCurrentContext } = get();
    
    if (!currentContext) {
      console.warn('No current context to refresh');
      return;
    }
    
    setError(null);
    
    try {
      const apiService = new RAGApiService(baseUrl);
      const refreshedContext = await apiService.getContextDetails(currentContext.contextId);
      setCurrentContext(refreshedContext);
    } catch (error) {
      console.error('Error refreshing current context:', error);
      setError(error instanceof Error ? error.message : 'Failed to refresh current context');
    }
  },

  setContextualRetrieval: async (enabled: boolean, reembed: boolean, onProgress?: (done: number, total: number) => void) => {
    const { setError, baseUrl, currentContext, refreshCurrentContext } = get();
    if (!currentContext) return;
    setError(null);
    try {
      const apiService = new RAGApiService(baseUrl);
      const res = await apiService.setContextualRetrieval(currentContext.contextId, enabled, reembed);
      const taskIds: string[] = (res?.queued || []).map((q: any) => q.task_id).filter(Boolean);
      if (reembed && taskIds.length > 0) {
        // Poll each re-embed task until terminal, reporting progress. Bounded so a
        // task that never terminates (worker down) can't hang the toggle forever;
        // a completed task evicted from the backend (404) is treated as terminal
        // after a few consecutive misses rather than looping indefinitely.
        const total = taskIds.length;
        const done = new Set<string>();
        const misses: Record<string, number> = {};
        const MAX_TICKS = 1800; // 1800 * 2s = 60 min hard ceiling
        let ticks = 0;
        onProgress?.(0, total);
        while (done.size < total && ticks++ < MAX_TICKS) {
          await new Promise((r) => setTimeout(r, 2000));
          for (const tid of taskIds) {
            if (done.has(tid)) continue;
            try {
              const st = (await apiService.getTaskStatus(tid))?.task?.status;
              if (st === 'completed' || st === 'failed') done.add(tid);
              else misses[tid] = 0;
            } catch {
              // 404 (or transient): a finished task is evicted after ~12min; treat
              // 3 consecutive misses as terminal so polling always converges.
              misses[tid] = (misses[tid] || 0) + 1;
              if (misses[tid] >= 3) done.add(tid);
            }
          }
          onProgress?.(done.size, total);
        }
      }
      await refreshCurrentContext();
    } catch (error) {
      console.error('Error setting contextual retrieval:', error);
      setError(error instanceof Error ? error.message : 'Failed to set contextual retrieval');
    }
  },


  createContext: async (name: string) => {
    const { setLoading, setError, baseUrl, setContexts } = get();
    setLoading(true);
    setError(null);
    
    try {
      const apiService = new RAGApiService(baseUrl);
      await apiService.createContext(name);
      
      // Reload contexts without resetting current context
      const contextIds = await apiService.listContexts();
      const contexts: RAGContext[] = [];
      
      for (const contextId of contextIds) {
        try {
          const context = await apiService.getContextDetails(contextId);
          contexts.push(context);
        } catch (error) {
          console.warn(`Failed to load context ${contextId}:`, error);
        }
      }
      
      setContexts(contexts);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to create context');
    } finally {
      setLoading(false);
    }
  },
  
  deleteContext: async (contextId: string) => {
    const { setLoading, setError, baseUrl, setContexts, setCurrentContext } = get();
    setLoading(true);
    setError(null);
    
    try {
      const apiService = new RAGApiService(baseUrl);
      await apiService.deleteContext(contextId);
      
      // Reload contexts and handle current context deletion
      const contextIds = await apiService.listContexts();
      const contexts: RAGContext[] = [];
      
      for (const contextId of contextIds) {
        try {
          const context = await apiService.getContextDetails(contextId);
          contexts.push(context);
        } catch (error) {
          console.warn(`Failed to load context ${contextId}:`, error);
        }
      }
      
      setContexts(contexts);
      
      // If current context was deleted, set first available context
      const { currentContext } = get();
      if (currentContext && !contexts.find(c => c.contextId === currentContext.contextId)) {
        if (contexts.length > 0) {
          setCurrentContext(contexts[0]);
        } else {
          setCurrentContext(null);
        }
      }
    } catch (error) {
      // Enhanced error handling with detailed information
      let errorMessage = 'Failed to delete context';
      
      if (error instanceof Error) {
        errorMessage = error.message;
        
        // Check if it's an HTTP error with additional details
        if ((error as any).isHttpError) {
          const status = (error as any).status;
          const data = (error as any).data;
          
          if (status >= 500) {
            errorMessage = `Server Error (${status}): ${error.message}`;
          } else if (status >= 400) {
            errorMessage = `Client Error (${status}): ${error.message}`;
          }
          
          // Log additional error details for debugging
          console.error('HTTP Error Details:', { status, data, originalError: error });
        }
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error && typeof error === 'object' && 'message' in error) {
        errorMessage = (error as any).message;
      }
      
      setError(errorMessage);
      console.error('Delete context error details:', error);
    } finally {
      setLoading(false);
    }
  },
  
  uploadFile: async (file: File) => {
    const { setError, currentContext, baseUrl, addFileUpload, updateFileUpload, setCurrentContext } = get();
    
    if (!currentContext) {
      setError('No context selected');
      return;
    }
    
    // Remove global operation check to allow concurrent uploads
    const uploadId = Date.now().toString();
    const upload: FileUpload = {
      id: uploadId,
      file,
      status: 'uploading',
      progress: 0,
      contextId: currentContext.contextId
    };
    
    addFileUpload(upload);
    setError(null);
    // Remove global operation state setting
    
    try {
      const apiService = new RAGApiService(baseUrl);
      const response = await apiService.uploadFiles(currentContext.contextId, [file], get().selectedLanguage, get().selectedChunkTool);
      
      updateFileUpload(uploadId, {
        status: 'success',
        progress: 100,
        response
      });
      
      // Call the upload success callback if it exists
      if (uploadSuccessCallback) {
        setTimeout(() => {
          uploadSuccessCallback!();
        }, 1000); // 1 second delay to ensure UI updates are complete
      }
      
          // Auto-refresh current context after successful upload
      setTimeout(async () => {
        try {
          const refreshedContext = await apiService.getContextDetails(currentContext.contextId);
          // Keep file upload state, only update context information
          const currentState = get();
          setCurrentContext({
            ...refreshedContext,
            // Keep the original file upload state
          });
          // Ensure file upload state is not overwritten
          set({ fileUploads: currentState.fileUploads });
          // Dispatch event to notify other components
          window.dispatchEvent(new CustomEvent('rag-operation-complete'));
        } catch (refreshError) {
          console.warn('Failed to auto-refresh context after upload:', refreshError);
        }
      }, 1000); // 1 second delay to avoid immediate refresh
    } catch (error) {
      // Enhanced error handling with detailed information
      let errorMessage = 'File upload failed';
      
      if (error instanceof Error) {
        errorMessage = error.message;
        
        // Check if it's an HTTP error with additional details
        if ((error as any).isHttpError) {
          const status = (error as any).status;
          const data = (error as any).data;
          
          if (status >= 500) {
            errorMessage = `Server Error (${status}): ${error.message}`;
          } else if (status >= 400) {
            errorMessage = `Client Error (${status}): ${error.message}`;
          }
          
          // Log additional error details for debugging
          console.error('HTTP Error Details:', { status, data, originalError: error });
        }
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error && typeof error === 'object' && 'message' in error) {
        errorMessage = (error as any).message;
      }
      
      updateFileUpload(uploadId, {
        status: 'error',
        error: errorMessage
      });
      setError(errorMessage);
      console.error('File upload error details:', error);
    } finally {
      // Remove global operation state reset
    }
  },
  
  uploadCustomJson: async (jsonData: CustomJsonData) => {
    const { setError, currentContext, baseUrl, setCurrentContext } = get();
    
    if (!currentContext) {
      setError('No context selected');
      return;
    }
    
    // Don't use global loading state for individual operations
    setError(null);
    
    try {
      const apiService = new RAGApiService(baseUrl);
      await apiService.uploadCustomJson(currentContext.contextId, jsonData.custom_text);
      
      // Auto-refresh current context after successful custom JSON upload
      setTimeout(async () => {
        try {
          const refreshedContext = await apiService.getContextDetails(currentContext.contextId);
          setCurrentContext(refreshedContext);
        } catch (refreshError) {
          console.warn('Failed to auto-refresh context after custom information upload:', refreshError);
        }
      }, 1000); // 1 second delay to avoid immediate refresh
    } catch (error) {
      // Enhanced error handling with detailed information
      let errorMessage = 'Failed to upload custom information';
      
      if (error instanceof Error) {
        errorMessage = error.message;
        
        // Check if it's an HTTP error with additional details
        if ((error as any).isHttpError) {
          const status = (error as any).status;
          const data = (error as any).data;
          
          if (status >= 500) {
            errorMessage = `Server Error (${status}): ${error.message}`;
          } else if (status >= 400) {
            errorMessage = `Client Error (${status}): ${error.message}`;
          }
          
          // Log additional error details for debugging
          console.error('HTTP Error Details:', { status, data, originalError: error });
        }
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error && typeof error === 'object' && 'message' in error) {
        errorMessage = (error as any).message;
      }
      
      setError(errorMessage);
      console.error('Upload custom information error details:', error);
    }
  },
  
  getCustomJson: async () => {
    const { setError, currentContext, baseUrl } = get();
    
    if (!currentContext) {
      setError('No context selected');
      return null;
    }
    
    // Don't use global loading state - let component manage its own loading
    setError(null);
    
    try {
      const apiService = new RAGApiService(baseUrl);
      return await apiService.getCustomJson(currentContext.contextId);
    } catch (error) {
      // Enhanced error handling with detailed information
      let errorMessage = 'Failed to get custom informaion';
      
      if (error instanceof Error) {
        errorMessage = error.message;
        
        // Check if it's an HTTP error with additional details
        if ((error as any).isHttpError) {
          const status = (error as any).status;
          const data = (error as any).data;
          
          if (status >= 500) {
            errorMessage = `Server Error (${status}): ${error.message}`;
          } else if (status >= 400) {
            errorMessage = `Client Error (${status}): ${error.message}`;
          }
          
          // Log additional error details for debugging
          console.error('HTTP Error Details:', { status, data, originalError: error });
        }
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error && typeof error === 'object' && 'message' in error) {
        errorMessage = (error as any).message;
      }
      
      setError(errorMessage);
      console.error('Get custom information error details:', error);
      return null;
    }
  },
  
  uploadProtectedKeywords: async (keywords: ProtectedKeywordsData) => {
    const { setError, currentContext, baseUrl, setCurrentContext } = get();
    
    if (!currentContext) {
      setError('No context selected');
      return;
    }
    
    // Don't use global loading state for individual operations
    setError(null);
    
    try {
      const apiService = new RAGApiService(baseUrl);
      await apiService.uploadProtectedKeywords(currentContext.contextId, keywords.protected);
      
      // Auto-refresh current context after successful protected keywords upload
      setTimeout(async () => {
        try {
          const refreshedContext = await apiService.getContextDetails(currentContext.contextId);
          setCurrentContext(refreshedContext);
        } catch (refreshError) {
          console.warn('Failed to auto-refresh context after protected keywords upload:', refreshError);
        }
      }, 1000); // 1 second delay to avoid immediate refresh
    } catch (error) {
      // Enhanced error handling with detailed information
      let errorMessage = 'Failed to upload protected keywords';
      
      if (error instanceof Error) {
        errorMessage = error.message;
        
        // Check if it's an HTTP error with additional details
        if ((error as any).isHttpError) {
          const status = (error as any).status;
          const data = (error as any).data;
          
          if (status >= 500) {
            errorMessage = `Server Error (${status}): ${error.message}`;
          } else if (status >= 400) {
            errorMessage = `Client Error (${status}): ${error.message}`;
          }
          
          // Log additional error details for debugging
          console.error('HTTP Error Details:', { status, data, originalError: error });
        }
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error && typeof error === 'object' && 'message' in error) {
        errorMessage = (error as any).message;
      }
      
      setError(errorMessage);
      console.error('Upload protected keywords error details:', error);
    }
  },
  
  uploadKeywordMappings: async (mappings: { mappings: Record<string, { patterns: string[]; replacement: string[] }> }) => {
    const { setError, currentContext, baseUrl, setCurrentContext } = get();
    
    if (!currentContext) {
      setError('No context selected');
      return;
    }
    
    // Don't use global loading state for individual operations
    setError(null);
    
    try {
      const apiService = new RAGApiService(baseUrl);
      await apiService.uploadKeywordMappings(currentContext.contextId, mappings.mappings);
      
      // Auto-refresh current context after successful keyword mappings upload
      setTimeout(async () => {
        try {
          const refreshedContext = await apiService.getContextDetails(currentContext.contextId);
          setCurrentContext(refreshedContext);
        } catch (refreshError) {
          console.warn('Failed to auto-refresh context after keyword mappings upload:', refreshError);
        }
      }, 1000); // 1 second delay to avoid immediate refresh
    } catch (error) {
      // Enhanced error handling with detailed information
      let errorMessage = 'Failed to upload keyword mappings';
      
      if (error instanceof Error) {
        errorMessage = error.message;
        
        // Check if it's an HTTP error with additional details
        if ((error as any).isHttpError) {
          const status = (error as any).status;
          const data = (error as any).data;
          
          if (status >= 500) {
            errorMessage = `Server Error (${status}): ${error.message}`;
          } else if (status >= 400) {
            errorMessage = `Client Error (${status}): ${error.message}`;
          }
          
          // Log additional error details for debugging
          console.error('HTTP Error Details:', { status, data, originalError: error });
        }
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error && typeof error === 'object' && 'message' in error) {
        errorMessage = (error as any).message;
      }
      
      setError(errorMessage);
      console.error('Upload keyword mappings error details:', error);
    }
  },
  
  downloadKeywordMappings: async () => {
    const { setError, currentContext, baseUrl } = get();
    
    if (!currentContext) {
      setError('No context selected');
      return;
    }
    
    setError(null);
    
    try {
      const apiService = new RAGApiService(baseUrl);
      const response = await apiService.downloadKeywordMappings(currentContext.contextId);
      
      // Create and download the file
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = response.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      let errorMessage = 'Failed to download keyword mappings';
      
      if (error instanceof Error) {
        errorMessage = error.message;
        
        // Check if it's an HTTP error with additional details
        if ((error as any).isHttpError) {
          const status = (error as any).status;
          const data = (error as any).data;
          
          if (status === 404) {
            errorMessage = 'Keyword mapping file not found for this context';
          } else if (status >= 500) {
            errorMessage = `Server Error (${status}): ${error.message}`;
          } else if (status >= 400) {
            errorMessage = `Client Error (${status}): ${error.message}`;
          }
          
          console.error('HTTP Error Details:', { status, data, originalError: error });
        }
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error && typeof error === 'object' && 'message' in error) {
        errorMessage = (error as any).message;
      }
      
      setError(errorMessage);
      console.error('Download keyword mappings error details:', error);
    }
  },

  getChunks: async (itemId: string) => {
    const { setError, baseUrl, currentContext, setCurrentContext } = get();
    
    if (!currentContext) {
      setError('No context selected');
      return;
    }
    
    // Remove global operation check to allow concurrent operations
    setError(null);
    // Remove global operation state setting
    
    try {
      const apiService = new RAGApiService(baseUrl);
      const result = await apiService.getChunk(currentContext.contextId, itemId);
      
      return result;
    } catch (error) {
      // Enhanced error handling with detailed information
      let errorMessage = 'Failed to get chunks';
      
      if (error instanceof Error) {
        errorMessage = error.message;
        
        // Check if it's an HTTP error with additional details
        if ((error as any).isHttpError) {
          const status = (error as any).status;
          const data = (error as any).data;
          
          if (status >= 500) {
            errorMessage = `Server Error (${status}): ${error.message}`;
          } else if (status >= 400) {
            errorMessage = `Client Error (${status}): ${error.message}`;
          }
          
          // Log additional error details for debugging
          console.error('HTTP Error Details:', { status, data, originalError: error });
        }
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error && typeof error === 'object' && 'message' in error) {
        errorMessage = (error as any).message;
      }
      
      setError(errorMessage);
      console.error('Build chunks error details:', error);
      throw error;
    } finally {
      // Remove global operation state reset
    }
  },

  uploadChunks: async (itemId: string, editChunk?: any) => {
    const { setError, baseUrl, currentContext } = get();
    
    if (!currentContext) {
      setError('No context selected');
      return;
    }
    
    setError(null);
    
    try {
      const apiService = new RAGApiService(baseUrl);
      const response = await apiService.uploadChunk(currentContext.contextId, itemId, editChunk);
      
      
      
    } catch (error) {
      // Enhanced error handling with detailed information
      let errorMessage = 'Failed to build embeddings';
      let shouldRefresh = false;
      
      if (error instanceof Error) {
        errorMessage = error.message;
        
        // Check if it's an HTTP error with additional details
        if ((error as any).isHttpError) {
          const status = (error as any).status;
          const data = (error as any).data;
          
          if (status === 409) {
            // 409 Conflict - file is being processed, this is not really an error
            // Extract detailed error message from server response
            let detailedMessage = 'File is currently being processed. Please wait and try again later.';
            
            if (data && data.detail) {
              const detail = data.detail;
              detailedMessage = `Failed to upload chunks: ${detail}`;
            }
            
            errorMessage = detailedMessage;
          } else if (status >= 500) {
            errorMessage = `Server Error (${status}): ${error.message}`;
          } else if (status >= 400) {
            errorMessage = `Client Error (${status}): ${error.message}`;
          }
          
          // Log additional error details for debugging
          console.error('HTTP Error Details:', { status, data, originalError: error });
        }
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error && typeof error === 'object' && 'message' in error) {
        errorMessage = (error as any).message;
      }
      
      setError(errorMessage);
      console.error('Uploading chunks error details:', error);
    }
  },
  
  buildChunks: async (itemId: string) => {
    const { setError, baseUrl, currentContext, setCurrentContext } = get();
    
    if (!currentContext) {
      setError('No context selected');
      return;
    }
    
    // Remove global operation check to allow concurrent operations
    setError(null);
    // Remove global operation state setting
    
    try {
      const apiService = new RAGApiService(baseUrl);
      const result = await apiService.buildChunk(currentContext.contextId, itemId, get().selectedChunkTool);
      
      // Auto-refresh current context after successful chunking
      try {
        const refreshedContext = await apiService.getContextDetails(currentContext.contextId);
        // Keep file upload state, only update context information
        const currentState = get();
        setCurrentContext({
          ...refreshedContext,
          // Keep the original file upload state
        });
        // Ensure file upload state is not overwritten
        set({ fileUploads: currentState.fileUploads });
        // Dispatch event to notify other components
        window.dispatchEvent(new CustomEvent('rag-operation-complete'));
      } catch (refreshError) {
        console.warn('Failed to auto-refresh context after chunking:', refreshError);
      }
      
      return result;
    } catch (error) {
      // Enhanced error handling with detailed information
      let errorMessage = 'Failed to build chunks';
      
      if (error instanceof Error) {
        errorMessage = error.message;
        
        // Check if it's an HTTP error with additional details
        if ((error as any).isHttpError) {
          const status = (error as any).status;
          const data = (error as any).data;
          
          if (status >= 500) {
            errorMessage = `Server Error (${status}): ${error.message}`;
          } else if (status >= 400) {
            errorMessage = `Client Error (${status}): ${error.message}`;
          }
          
          // Log additional error details for debugging
          console.error('HTTP Error Details:', { status, data, originalError: error });
        }
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error && typeof error === 'object' && 'message' in error) {
        errorMessage = (error as any).message;
      }
      
      setError(errorMessage);
      console.error('Build chunks error details:', error);
      throw error;
    } finally {
      // Remove global operation state reset
    }
  },

  getQueueStatus: async () => {
    const { setError, baseUrl } = get();
    setError(null);

    try {
      const apiService = new RAGApiService(baseUrl);
      const data = await apiService.getQueueStatus();
      set({ queueStatus: data.queue_status }); // Assume response is { queue_status: {...} }
    } catch (error) {
      console.error('Error getting queue status:', error);
      setError(error instanceof Error ? error.message : 'Failed to get queue status');
    }
  },

  getAllTasks: async (limit: number = 50) => {
    const { setError, baseUrl } = get();
    setError(null);

    try {
      const apiService = new RAGApiService(baseUrl);
      const data = await apiService.getAllTasks(limit);
      return data.tasks; // Assume response structure is { tasks: [...] }
    } catch (error) {
      console.error("Error getting tasks:", error);
      setError(error instanceof Error ? error.message : "Failed to get tasks");
      return [];
    }
  },
  
  buildEmbeddings: async (itemId: string, editChunk?: any) => {
    const { setError, baseUrl, currentContext } = get();
    
    if (!currentContext) {
      setError('No context selected');
      return;
    }
    
    setError(null);
    
    try {
      const apiService = new RAGApiService(baseUrl);
      const response = await apiService.buildEmbed(currentContext.contextId, itemId, editChunk);
      
      // Handle the new queue response format
      if (response.status === 'Queued' || response.status === 'Already Queued') {
        // Show success message with task information
        const message = response.status === 'Already Queued' 
          ? `Embed task already exists in queue (Task ID: ${response.task_id})`
          : `Embed task has been queued successfully (Task ID: ${response.task_id})`;
        
        // Show success message for queue submission
        alert(message + '\n\nNote: The task is running in the background. The page will automatically refresh when the task completes.');
        
        // Immediately update file status to show queued/processing state
        await get().refreshCurrentContext();
        
        // Start long polling directly without checking initial status
        // This avoids the 404 error when task is created but not yet in queue
        console.log(`[TASK POLLING] Starting long poll for task ${response.task_id}`);
        const pollingService = getTaskPollingService(apiService);
        const pollingOptions: TaskPollingOptions = {
          timeout: 600, // 600 seconds (10 minutes) timeout as requested
          pollInterval: 2.0, // Check every 2 seconds
          onComplete: async (task) => {
            console.log(`[TASK POLLING] Task ${task.task_id} completed successfully`);
            // Refresh the context to show updated file status
            await get().refreshCurrentContext();
            alert(`Embed task completed successfully! File status has been updated.`);
          },
          onError: (error) => {
            console.error(`[TASK POLLING] Error polling task ${response.task_id}:`, error);
            // Check if it's a 404 error (task not found)
            if (error.message.includes('404') || error.message.includes('not found')) {
              console.log(`[TASK POLLING] Task ${response.task_id} not found, may have been completed and cleaned up`);
              // Refresh to show current status - task may have completed
              get().refreshCurrentContext();
            } else if (error.message.includes('504') || error.message.includes('Gateway Time-out')) {
              console.log(`[TASK POLLING] Task ${response.task_id} polling timed out due to gateway timeout`);
              // Refresh to show current status and inform user
              get().refreshCurrentContext();
              alert(`Task polling timed out due to server timeout. The page has been refreshed to show current status.`);
            } else {
              // Still refresh to show current status
              get().refreshCurrentContext();
            }
          },
          onTimeout: async (task) => {
            console.log(`[TASK POLLING] Task ${task.task_id} polling timed out`);
            // Refresh to show current status even if polling timed out
            await get().refreshCurrentContext();
            alert(`Embed task is still running. The page has been refreshed to show current status.`);
          }
        };
        
        // Start polling in the background (don't await to avoid blocking UI)
        pollingService.pollTask(response.task_id, pollingOptions).catch(error => {
          console.error(`[TASK POLLING] Unexpected error in polling:`, error);
        });
        
      } else {
        // Handle unexpected response format
        alert('Embed request submitted, but response format was unexpected');
      }
      
    } catch (error) {
      // Enhanced error handling with detailed information
      let errorMessage = 'Failed to build embeddings';
      let shouldRefresh = false;
      
      if (error instanceof Error) {
        errorMessage = error.message;
        
        // Check if it's an HTTP error with additional details
        if ((error as any).isHttpError) {
          const status = (error as any).status;
          const data = (error as any).data;
          
          if (status === 409) {
            // 409 Conflict - file is being processed, this is not really an error
            // Extract detailed error message from server response
            let detailedMessage = 'File is currently being processed. Please wait and try again later.';
            
            if (data && data.detail) {
              const detail = data.detail;
              if (detail.includes('currently being processed')) {
                detailedMessage = 'This file is currently being processed by another operation. Please wait for it to complete before trying again.';
              } else if (detail.includes('has pending operations')) {
                detailedMessage = 'This file has pending operations in the queue. Please wait for them to complete before trying again.';
              } else if (detail.includes('has a pending embed task')) {
                detailedMessage = 'This file already has an embed task in the queue. Please wait for it to complete before trying again.';
              } else {
                detailedMessage = `Operation blocked: ${detail}`;
              }
            }
            
            errorMessage = detailedMessage;
            shouldRefresh = true; // Refresh to show current status
          } else if (status >= 500) {
            errorMessage = `Server Error (${status}): ${error.message}`;
          } else if (status >= 400) {
            errorMessage = `Client Error (${status}): ${error.message}`;
          }
          
          // Log additional error details for debugging
          console.error('HTTP Error Details:', { status, data, originalError: error });
        }
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error && typeof error === 'object' && 'message' in error) {
        errorMessage = (error as any).message;
      }
      
      // For 409 errors, show a warning instead of error and refresh the context
      if (shouldRefresh) {
        console.warn('File is being processed:', errorMessage);
        // Show warning instead of error
        alert(`Warning: ${errorMessage}`);
        // Always refresh UI to get current file status
        await get().refreshCurrentContext();
      } else {
        setError(errorMessage);
        console.error('Build embeddings error details:', error);
        // Even on error, refresh UI to show current status
        await get().refreshCurrentContext();
      }
    }
  },
  
  deleteItem: async (itemId: string) => {
    const { setError, baseUrl, currentContext, setCurrentContext } = get();
    
    if (!currentContext) {
      setError('No context selected');
      return;
    }
    
    // Remove global operation check to allow concurrent operations
    setError(null);
    // Remove global operation state setting
    
    try {
      const apiService = new RAGApiService(baseUrl);
      await apiService.deleteRAG(currentContext.contextId, itemId);
      
      // Update current context by removing the deleted item
      const updatedRagItems = currentContext.ragItems.filter(item => item.ragId !== itemId);
      const updatedContext = {
        ...currentContext,
        ragItems: updatedRagItems
      };
      setCurrentContext(updatedContext);
      
      // Auto-refresh current context after successful deletion
      setTimeout(async () => {
        try {
          const refreshedContext = await apiService.getContextDetails(currentContext.contextId);
          // Keep file upload state, only update context information
          const currentState = get();
          setCurrentContext({
            ...refreshedContext,
            // Keep the original file upload state
          });
          // Ensure file upload state is not overwritten
          set({ fileUploads: currentState.fileUploads });
          // Dispatch event to notify other components
          window.dispatchEvent(new CustomEvent('rag-operation-complete'));
        } catch (refreshError) {
          console.warn('Failed to auto-refresh context after deletion:', refreshError);
        }
      }, 1000); // 1 second delay to avoid immediate refresh
    } catch (error) {
      // Enhanced error handling with detailed information
      let errorMessage = 'Failed to delete item';
      
      if (error instanceof Error) {
        errorMessage = error.message;
        
        // Check if it's an HTTP error with additional details
        if ((error as any).isHttpError) {
          const status = (error as any).status;
          const data = (error as any).data;
          
          if (status >= 500) {
            errorMessage = `Server Error (${status}): ${error.message}`;
          } else if (status >= 400) {
            errorMessage = `Client Error (${status}): ${error.message}`;
          }
          
          // Log additional error details for debugging
          console.error('HTTP Error Details:', { status, data, originalError: error });
        }
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error && typeof error === 'object' && 'message' in error) {
        errorMessage = (error as any).message;
      }
      
      setError(errorMessage);
      console.error('Delete item error details:', error);
    } finally {
      // Remove global operation state reset
    }
  },
  
  query: async (request: QueryRequest) => {
    const { setError, setLastUserQuery, setLastResponse, baseUrl, currentContext } = get();
    
    if (!currentContext) {
      setError('No context selected');
      return;
    }
    
    // Remove global operation check to allow concurrent queries
    setError(null);
    setLastUserQuery(request.query);
    // Remove global operation state setting
    
    try {
      const apiService = new RAGApiService(baseUrl);
      const response = await apiService.query(currentContext.contextId, request);
      setLastResponse(response);
    } catch (error) {
      // Enhanced error handling with detailed information
      let errorMessage = 'Query failed';
      
      if (error instanceof Error) {
        errorMessage = error.message;
        
        // Check if it's an HTTP error with additional details
        if ((error as any).isHttpError) {
          const status = (error as any).status;
          const data = (error as any).data;
          
          if (status >= 500) {
            errorMessage = `Server Error (${status}): ${error.message}`;
          } else if (status >= 400) {
            errorMessage = `Client Error (${status}): ${error.message}`;
          }
          
          // Log additional error details for debugging
          console.error('HTTP Error Details:', { status, data, originalError: error });
        }
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error && typeof error === 'object' && 'message' in error) {
        errorMessage = (error as any).message;
      }
      
      setError(errorMessage);
      console.error('Query error details:', error);
    } finally {
      // Remove global operation state reset
    }
  },
  
  // Utility Actions
  clearCurrentContext: () => {
    set({ 
      currentContext: null,
      lastUserQuery: '',
      lastResponse: null,
      // Don't clear fileUploads, keep file upload state
    });
  },
}));
